import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  Search,
  CalendarClock,
  Car,
  FileText,
  Building2,
  ShieldCheck,
  Wallet,
} from "lucide-react";

import { getClients } from "../api";

import { useNavigate } from "react-router-dom";

import "../styles/upcomingrenewals.css";

const UpcomingRenewals = () => {
  const [clients, setClients] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const navigate = useNavigate();

const [showAllRenewals, setShowAllRenewals] =
  useState(false);


  const upcomingRenewalsGetMonthRange =
    () => {
      const now = new Date();

      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        1
      );

      const end = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      );

      return { start, end };
    };

  const upcomingRenewalsFormatDate =
    (dateString) => {
      if (!dateString)
        return "N/A";

      const d = new Date(
        dateString
      );

      if (isNaN(d.getTime()))
        return "N/A";

      return d.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    };

  useEffect(() => {
    const fetchClients =
      async () => {
        const data =
          await getClients();

        const {
          start,
          end,
        } =
          upcomingRenewalsGetMonthRange();

        const upcoming =
          data.filter(
            (client) => {
              if (
                !client.renewal_date
              )
                return false;

              const date =
                new Date(
                  client.renewal_date
                );

              return (
                date >= start &&
                date <= end
              );
            }
          );

        setClients(upcoming);
      };

    fetchClients();
  }, []);

  const upcomingRenewalsFilteredClients =
    useMemo(() => {
      return clients
        .filter(
          (c) =>
            c.name
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            c.policy_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            c.vehicle_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              )
        )

        .sort((a, b) =>
          a.name.localeCompare(
            b.name
          )
        );
    }, [clients, searchTerm]);

const upcomingRenewalsDisplayedClients =
  searchTerm.trim()
    ? upcomingRenewalsFilteredClients
    : showAllRenewals
    ? upcomingRenewalsFilteredClients
    : upcomingRenewalsFilteredClients.slice(
        0,
        10
      );

  return (
    <div className="upcoming-renewals-modern-page">

      {/* HERO */}

      <div className="upcoming-renewals-modern-hero">

        <div className="upcoming-renewals-modern-left">

          <div className="upcoming-renewals-alert-pill">
            <CalendarClock size={16} />
            Renewal Tracker
          </div>

          <h1 className="upcoming-renewals-heading">
            Upcoming
            Renewals.
          </h1>

          <p className="upcoming-renewals-description">
            Monitor insurance
            policies approaching
            renewal dates and
            track upcoming client
            coverage activity.
          </p>

          <div className="upcoming-renewals-actions">

            <button
              className="upcoming-renewals-back-button"
              onClick={() =>
                navigate(
                  "/insurance"
                )
              }
            >
              <ArrowLeft size={18} />
              Back to Insurance
            </button>
          </div>
        </div>

        {/* STATS */}

        <div className="upcoming-renewals-stats-grid">

          <div className="upcoming-renewals-stat-card">

            <div className="upcoming-renewals-stat-icon teal">
              <CalendarClock size={22} />
            </div>

            <div>
              <span>
                Renewals This
                Month
              </span>

              <h2>
                {
                  clients.length
                }
              </h2>
            </div>
          </div>

          <div className="upcoming-renewals-stat-card">

            <div className="upcoming-renewals-stat-icon blue">
              <ShieldCheck size={22} />
            </div>

            <div>
              <span>
                Displayed
              </span>

              <h2>
                {
                  upcomingRenewalsDisplayedClients.length
                }
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}

      <div className="upcoming-renewals-toolbar">

        <div className="upcoming-renewals-search-box">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search client, vehicle or policy number..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />
        </div>

<div className="upcoming-renewals-toolbar-right">

  <div className="upcoming-renewals-results">
    {searchTerm.trim()
      ? `${upcomingRenewalsFilteredClients.length} client(s) found`
      : showAllRenewals
      ? `Showing all renewals`
      : `Showing top 10 renewals`}
  </div>

  {!searchTerm.trim() && (
    <button
      className="upcoming-renewals-toggle-btn"
      onClick={() =>
        setShowAllRenewals(
          !showAllRenewals
        )
      }
    >
      {showAllRenewals
        ? "Collapse Table"
        : "View All"}
    </button>
  )}
</div>
      </div>

      {/* TABLE */}

      <div className="upcoming-renewals-table-wrapper">

        <table className="upcoming-renewals-table">

          <thead>
            <tr>

              <th>
                Client
              </th>

              <th>
                Policy
              </th>

              <th>
                Vehicle
              </th>

              <th>
                Premium
              </th>

              <th>
                Insurer
              </th>

              <th>
                Renewal Date
              </th>

              <th>
                Status
              </th>

            </tr>
          </thead>

          <tbody>

            {upcomingRenewalsDisplayedClients.length >
            0 ? (
              upcomingRenewalsDisplayedClients.map(
                (
                  c,
                  i
                ) => (
                  <tr
                    key={i}
                  >

                    {/* CLIENT */}

                    <td>

                      <div className="upcoming-renewals-client-cell">

                        <div className="upcoming-renewals-avatar">
                          {c.name
                            ?.charAt(
                              0
                            )
                            ?.toUpperCase()}
                        </div>

                        <div>
                          <h4>
                            {
                              c.name
                            }
                          </h4>

                          <span>
                            {
                              c.policy_type
                            }
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* POLICY */}

                    <td>

                      <div className="upcoming-renewals-info-cell">

                        <FileText size={16} />

                        <span>
                          {
                            c.policy_number
                          }
                        </span>

                      </div>
                    </td>

                    {/* VEHICLE */}

                    <td>

                      <div className="upcoming-renewals-info-cell">

                        <Car size={16} />

                        <span>
                          {
                            c.vehicle_number
                          }
                        </span>

                      </div>
                    </td>

                    {/* PREMIUM */}

                    <td className="upcoming-renewals-amount">
                      $
                      {parseFloat(
                        c.premium ||
                          0
                      ).toLocaleString()}
                    </td>

                    {/* INSURER */}

                    <td>

                      <div className="upcoming-renewals-info-cell">

                        <Building2 size={16} />

                        <span>
                          {
                            c.insurer
                          }
                        </span>

                      </div>
                    </td>

                    {/* DATE */}

                    <td>
                      {upcomingRenewalsFormatDate(
                        c.renewal_date
                      )}
                    </td>

                    {/* STATUS */}

                    <td>

                      <span className="upcoming-renewals-status-pill">
                        Upcoming
                      </span>

                    </td>

                  </tr>
                )
              )
            ) : (
              <tr>

                <td
                  colSpan="7"
                  className="upcoming-renewals-empty-state"
                >

                  <CalendarClock
                    size={44}
                  />

                  <h3>
                    No Upcoming
                    Renewals
                  </h3>

                  <p>
                    No insurance
                    policies are
                    due for renewal
                    this month.
                  </p>

                </td>

              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UpcomingRenewals;